import React, { useState, useRef } from "react";
import { readExcel } from "../../../../utils/excelfunction";
import { isFileFormatSupported } from "../../../../utils/importElevesUtils";
import { analyserFichierGroupes, colonnesManquantes, libelleStatut, normaliser, STATUTS_SANS_ALERTE } from "../../../../utils/importGroupes";
import { usePostApi } from "../../../../hooks/post_api_hook/usePostApi";
import { url } from "../../../../utils/confserveur";
import { X, Upload, AlertTriangle, CheckCircle, XCircle, Loader2 } from "lucide-react";

/**
 * Import du fichier d'affectation (Nom, Prénom, Classe, Groupe).
 *
 * `listGroupes` : les GROUPES DE BESOIN de l'établissement (2026-09-30), les
 * seuls noms du fichier que l'import reconnaît ; un demi-groupe est ignoré
 * comme une langue ou une option. La lecture (noms inconnus ignorés et
 * listés, lignes incomplètes écartées avec les autres lignes de l'élève) est
 * dans utils/importGroupes.js ; le serveur applique « la ligne fait foi ».
 */
function ImportElevesGroupe({ show, handleClose, listGroupes, etablissementData, onSuccess }) {
    const [fileLoad, setFileLoad] = useState(false);
    // Résultat de analyserFichierGroupes : lignes, affectations, élèves non modifiés.
    const [analyse, setAnalyse] = useState(null);
    const [errorLoad, setErrorLoad] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    const [{ isLoading: isLoadingPost }, doPost] = usePostApi(url + "groupes/initialisation-groupe-eleves/");

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            setSelectedFile(files[0]);
            handleFiles(files[0]);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
            handleFiles(e.target.files[0]);
        }
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    // Traite le fichier uploadé
    const handleFiles = async (file) => {
        if (!file || !(file instanceof Blob)) {
            setErrorLoad("Aucune donnée n'est présente dans ce fichier.");
            return;
        }

        if (!isFileFormatSupported(file.name)) {
            setErrorLoad(`Format de fichier non supporté. Veuillez sélectionner un fichier CSV, Excel (.xlsx, .xls) ou OpenDocument (.ods).`);
            setFileLoad(false);
            return;
        }

        try {
            const sheetsData = await readExcel(file);

            if (!sheetsData || !sheetsData[0] || !sheetsData[0][0]) {
                setFileLoad(false);
                setErrorLoad("Aucune donnée valide trouvée dans ce fichier.");
                return;
            }

            const firstRow = sheetsData[0][0];
            const headers = Object.keys(firstRow);

            const manquantes = colonnesManquantes(headers);
            if (manquantes.length > 0) {
                setFileLoad(false);
                setErrorLoad(`Colonne(s) manquante(s) : ${manquantes.join(', ')}. Attendu : Groupe, Nom, Prénom, Classe. Trouvé : ${headers.join(', ')}`);
                return;
            }

            // Lignes vides ignorées, lignes à problème listées (leur élève n'est
            // pas envoyé) : le reste du fichier s'importe (utils/importGroupes.js).
            const resultat = analyserFichierGroupes(sheetsData[0], {
                groupes: listGroupes,
                classes: etablissementData.classes,
            });

            if (resultat.lignes.length === 0) {
                setFileLoad(false);
                setErrorLoad("Aucune donnée valide trouvée dans ce fichier.");
                return;
            }

            setAnalyse(resultat);
            setFileLoad(true);
            setErrorLoad('');

        } catch (error) {
            console.error("Erreur lors de la lecture du fichier:", error);
            setErrorLoad("Erreur lors de la lecture du fichier.");
            setFileLoad(false);
        }
    };

    // Les élèves non modifiés : ceux que le front n'a pas envoyés (ligne à
    // problème) et ceux que le serveur a écartés (une ligne rejetée : groupe
    // disparu, hors barrette), un par élève.
    const fusionnerNonModifies = (ecartesServeur) => {
        const liste = [...(analyse?.nonModifies || [])];
        const vus = new Set(liste.map((e) => `${normaliser(e.nom)}|${normaliser(e.prenom)}|${normaliser(e.classe)}`));
        (ecartesServeur || []).forEach((e) => {
            const cle = `${normaliser(e.nom)}|${normaliser(e.prenom)}|${normaliser(e.classe)}`;
            if (vus.has(cle)) return;
            vus.add(cle);
            liste.push({ ...e, raison: 'ligne refusée par le serveur (groupe disparu ou hors barrette)' });
        });
        return liste;
    };

    // Soumet les données au serveur
    const handleSubmit = async () => {
        const affectations = analyse?.affectations || [];

        if (affectations.length === 0) {
            setErrorLoad("Aucun élève à modifier : toutes les lignes sont à corriger.");
            return;
        }

        try {
            const payload = { affectations, etablissement_id: etablissementData.id };
            const response = await doPost(payload);

            // Cas succès 200
            if (response && !response.error) {
                const data = response.data || response;
                const nbAffectes = data.eleves_affectes?.length || 0;
                const nbRetraits = data.nb_retraits || 0;
                const nbNonTrouves = data.eleves_non_trouves?.length || 0;
                const nbHorsBarette = data.eleves_hors_barette?.length || 0;
                const nbAnomalies = nbNonTrouves + nbHorsBarette;
                const retraits = nbRetraits > 0 ? ` et ${nbRetraits} retrait(s) d'un groupe` : '';

                // Affichage structuré dans tous les cas (succès complet ou partiel).
                // onSuccess() n'est PAS appelé ici — déféré au clic "Fermer" pour ne pas
                // déclencher de re-render parent pendant que l'utilisateur lit le rapport.
                // « La ligne fait foi » : un groupe oublié sur une ligne devient un
                // retrait, le délégué doit le voir (nb_retraits, servi par le back).
                setImportResult({
                    success: true,
                    message: nbAnomalies === 0
                        ? `${nbAffectes} affectation(s) de groupe enregistrée(s)${retraits}.`
                        : `${nbAffectes} affectation(s) de groupe enregistrée(s)${retraits}. ${nbAnomalies} anomalie(s) détectée(s).`,
                    nbRetraits,
                    elevesAffectes: data.eleves_affectes || [],
                    elevesNonTrouves: data.eleves_non_trouves || [],
                    elevesHorsBarette: data.eleves_hors_barette || [],
                    elevesNonModifies: fusionnerNonModifies(data.eleves_ecartes),
                });
                setErrorLoad('');
            }
            // Cas erreur 404 - aucun élève affecté
            else if (response?.error?.response?.status === 404) {
                const errorData = response.error.response.data;
                setImportResult({
                    success: false,
                    message: errorData.error || "Aucun élève n'a pu être affecté",
                    nbRetraits: 0,
                    elevesAffectes: [],
                    elevesNonTrouves: errorData.eleves_non_trouves || [],
                    elevesHorsBarette: errorData.eleves_hors_barette || [],
                    elevesNonModifies: fusionnerNonModifies(errorData.eleves_ecartes),
                });
                setErrorLoad('');
            }
            // Autres erreurs
            else {
                const errorMessage = response?.error?.response?.data?.error ||
                    response?.error?.message ||
                    "Une erreur est survenue lors de l'import.";
                setErrorLoad(errorMessage);
                setImportResult(null);
            }
        } catch (error) {
            console.error("Erreur lors de l'import:", error);
            setErrorLoad("Erreur lors de l'envoi des données.");
            setImportResult(null);
        }
    };

    const handleReset = () => {
        setSelectedFile(null);
        setFileLoad(false);
        setAnalyse(null);
        setErrorLoad('');
        setImportResult(null);
    };

    // Fermeture de la modale : on déclenche le refresh côté parent
    // uniquement s'il y a eu un import effectif (un élève affecté ou retiré).
    // Sinon, fermeture simple (cas du bouton Annuler avant soumission).
    const handleCloseWithRefresh = () => {
        const aImportEffectif = importResult?.success
            && ((importResult.elevesAffectes?.length || 0) > 0 || (importResult.nbRetraits || 0) > 0);
        if (aImportEffectif) {
            onSuccess && onSuccess();
        }
        handleClose();
    };

    // Stats pour l'affichage
    const stats = {
        lignes: analyse?.lignes.length || 0,
        envoyes: analyse?.nbElevesEnvoyes || 0,
        nonModifies: analyse?.nonModifies.length || 0,
        // Une ligne sans groupe n'est pas une erreur (liste de classe complète) :
        // l'élève garde ses groupes, sans alerte.
        enErreur: analyse?.lignes.filter((l) => l.statut !== 'ok' && !STATUTS_SANS_ALERTE.has(l.statut)).length || 0,
    };

    // « Import réussi » seulement si aucune ligne en erreur n'a été laissée de
    // côté, ni par le front ni par le serveur.
    const importComplet = Boolean(importResult?.success)
        && (importResult.elevesNonTrouves?.length || 0) === 0
        && (importResult.elevesHorsBarette?.length || 0) === 0
        && (importResult.elevesNonModifies || []).every((e) => STATUTS_SANS_ALERTE.has(e.statut));

    if (!show) return null;

    // Couleurs du thème
    const colors = {
        gradient: 'from-blue-600 via-cyan-500 to-sky-400',
        blob1: '#67e8f9',
        blob2: '#a5f3fc',
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header avec gradient et blobs animés */}
                <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-4 sm:px-8 xl:px-6 2xl:px-7 3xl:px-8 py-6 sm:py-10 xl:py-6 2xl:py-8 3xl:py-10">
                    {/* Bouton fermer */}
                    <button
                        onClick={handleCloseWithRefresh}
                        className="absolute top-2 right-2 sm:top-4 sm:right-4 z-20 p-1.5 sm:p-2 bg-orange-500 backdrop-blur-sm rounded-lg sm:rounded-xl text-white hover:bg-orange-600 transition-colors"
                    >
                        <X className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>

                    {/* Blobs animés */}
                    <svg className="absolute top-0 left-0 w-full h-full opacity-30 hidden sm:block" viewBox="0 0 200 200">
                        <circle cx="20" cy="20" r="40" fill={colors.blob1}>
                            <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
                            <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
                        </circle>
                        <circle cx="180" cy="80" r="30" fill={colors.blob2}>
                            <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
                            <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
                        </circle>
                        <circle cx="100" cy="150" r="35" fill="#22d3ee">
                            <animate attributeName="cx" values="100;130;100" dur="10s" repeatCount="indefinite" />
                            <animate attributeName="cy" values="150;170;150" dur="5s" repeatCount="indefinite" />
                        </circle>
                    </svg>

                    <div className="relative z-10 text-center">
                        <div className="flex justify-center mb-2 sm:mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
                            <div className="p-2.5 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl">
                                <Upload className="text-white w-6 h-6 sm:w-10 sm:h-10 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10" />
                            </div>
                        </div>
                        <h1 className="text-xl sm:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-white mb-1 sm:mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2">
                            Affectation initiale
                        </h1>
                        <p className="text-cyan-100 text-sm sm:text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
                            Peupler les groupes de besoins depuis un fichier Excel
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 xl:p-4 2xl:p-5 3xl:p-6 overflow-y-auto flex-1 relative">
                    {/* Loader inline pendant l'import */}
                    {isLoadingPost && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-xl">
                            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-3" />
                            <p className="text-blue-600 font-medium">Import en cours...</p>
                        </div>
                    )}

                    {/* Zone de drag & drop */}
                    <div
                        className={`relative border-2 border-dashed rounded-lg sm:rounded-xl p-4 sm:p-6 xl:p-4 2xl:p-5 3xl:p-6 mb-3 sm:mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4 transition-all duration-300 cursor-pointer ${
                            dragOver
                                ? 'border-blue-500 bg-blue-50 scale-[1.02]'
                                : selectedFile && !errorLoad
                                    ? 'border-emerald-400 bg-emerald-50'
                                    : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={openFileDialog}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileInputChange}
                            accept=".csv,.xlsx,.xls,.ods"
                            className="hidden"
                        />

                        <div className="text-center">
                            <div className={`w-12 h-12 sm:w-16 sm:h-16 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 mx-auto mb-2 sm:mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3 rounded-lg sm:rounded-xl flex items-center justify-center ${
                                selectedFile && !errorLoad
                                    ? 'bg-emerald-500'
                                    : 'bg-gradient-to-br from-blue-500 to-sky-600'
                            }`}>
                                {selectedFile && !errorLoad ? (
                                    <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                                ) : (
                                    <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                                )}
                            </div>

                            {selectedFile && !errorLoad ? (
                                <div>
                                    <p className="text-emerald-700 font-semibold mb-1 text-sm sm:text-base">Fichier sélectionné !</p>
                                    <p className="text-emerald-600 text-xs sm:text-sm bg-emerald-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg inline-block break-all">
                                        📄 {selectedFile.name}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <p className={`font-semibold mb-1 text-sm sm:text-base ${dragOver ? 'text-blue-600' : 'text-slate-700'}`}>
                                        {dragOver ? 'Déposez votre fichier ici !' : 'Cliquez ou glissez-déposez votre fichier'}
                                    </p>
                                    <p className="text-slate-500 text-xs sm:text-sm">Formats acceptés : CSV, Excel, ODS</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info colonnes attendues */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4 mb-3 sm:mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                        <p className="text-blue-800 font-medium mb-1.5 sm:mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2 text-xs sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base">Colonnes attendues :</p>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                            {['Groupe', 'Nom', 'Prénom', 'Classe'].map(col => (
                                <span key={col} className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-0.5 sm:py-1 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium">
                                    {col}
                                </span>
                            ))}
                        </div>
                        <p className="text-blue-600 text-[10px] sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm mt-1.5 sm:mt-2 xl:mt-1.5 2xl:mt-1.5 3xl:mt-2">
                            💡 La colonne "Groupe" peut contenir plusieurs groupes séparés par des virgules
                        </p>
                        <p className="text-blue-600 text-[10px] sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm mt-1">
                            Chaque ligne donne tous les groupes de l'élève : il quitte les groupes de besoin des mêmes matières qui n'y figurent pas. Les groupes d'une seule classe ne sont jamais vidés.
                        </p>
                    </div>

                    {/* Message d'erreur */}
                    {errorLoad && (
                        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg sm:rounded-xl p-3 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4 mb-3 sm:mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4 flex items-start gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                            <div className="p-1.5 sm:p-2 bg-orange-500 rounded-lg flex-shrink-0">
                                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-orange-800 font-semibold mb-1 text-sm sm:text-base">Erreur</p>
                                <p className="text-orange-700 text-xs sm:text-sm whitespace-pre-wrap">{errorLoad}</p>
                            </div>
                        </div>
                    )}

                    {/* Prévisualisation des données — masquée dès qu'un rapport d'import est affiché */}
                    {fileLoad && analyse && !importResult && (
                        <div className="mt-3 sm:mt-4 xl:mt-3 2xl:mt-3.5 3xl:mt-4 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                            {/* Stats */}
                            <div className="flex flex-wrap gap-2 sm:gap-4 xl:gap-2 2xl:gap-3 3xl:gap-4 mb-3 sm:mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                                <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-2.5 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 bg-slate-100 rounded-lg sm:rounded-xl">
                                    <span className="text-slate-600">Lignes :</span>
                                    <span className="font-bold text-slate-800">{stats.lignes}</span>
                                </div>
                                <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-2.5 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 bg-emerald-100 rounded-lg sm:rounded-xl">
                                    <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                                    <span className="text-emerald-600">Élèves à modifier :</span>
                                    <span className="font-bold text-emerald-700">{stats.envoyes}</span>
                                </div>
                                {stats.nonModifies > 0 && (
                                    <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-2.5 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 bg-orange-100 rounded-lg sm:rounded-xl">
                                        <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600" />
                                        <span className="text-orange-600">Non modifiés :</span>
                                        <span className="font-bold text-orange-700">{stats.nonModifies}</span>
                                    </div>
                                )}
                            </div>
                            {stats.enErreur > 0 && (
                                <p className="text-orange-700 mb-3">
                                    Les élèves des lignes en orange ne seront pas modifiés : ils gardent leurs groupes actuels. Corrigez ces lignes et réimportez le fichier si besoin.
                                </p>
                            )}

                            {/* Noms de groupes ignorés : une info, pas un blocage. C'est là
                                que le délégué voit une faute (« 6emeG33 (1) » au milieu des
                                langues et options de l'export Pronote). */}
                            {analyse.nomsIgnores.length > 0 && (
                                <div className="bg-slate-50 border border-slate-200 rounded-lg sm:rounded-xl p-3 mb-3 text-slate-700">
                                    <p className="font-semibold mb-1">Noms de groupes ignorés (inconnus dans TanoEd)</p>
                                    <p>
                                        {analyse.nomsIgnores
                                            .map(({ nom, eleves }) => `${nom} (${eleves} élève${eleves > 1 ? 's' : ''})`)
                                            .join(', ')}
                                    </p>
                                    <p className="text-slate-500 mt-1">
                                        Langues et options sont normales ici. Si l'un de ces noms est un groupe de besoin mal écrit, l'élève quittera son groupe de cette matière : corrigez le fichier et réimportez-le.
                                    </p>
                                </div>
                            )}

                            {/* Table */}
                            <div className="border border-slate-200 rounded-lg sm:rounded-xl overflow-hidden">
                                <div className="overflow-x-auto max-h-48 sm:max-h-60 xl:max-h-48 2xl:max-h-52 3xl:max-h-60">
                                    <table className="w-full text-[10px] sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm min-w-[500px]">
                                        <thead className="bg-gradient-to-r from-blue-500 to-sky-600 text-white sticky top-0">
                                            <tr>
                                                <th className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left font-semibold">Ligne</th>
                                                <th className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left font-semibold">Nom</th>
                                                <th className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left font-semibold">Prénom</th>
                                                <th className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left font-semibold">Classe</th>
                                                <th className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left font-semibold">Groupes</th>
                                                <th className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left font-semibold">Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analyse.lignes.map((item) => (
                                                <tr key={item.ligne} className={`border-b border-slate-100 ${
                                                    item.statut === 'ok' ? 'hover:bg-emerald-50'
                                                        : STATUTS_SANS_ALERTE.has(item.statut) ? 'text-slate-500'
                                                            : 'bg-orange-50 hover:bg-orange-100'
                                                }`}>
                                                    <td className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-slate-500 tabular-nums">{item.ligne}</td>
                                                    <td className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 font-semibold text-slate-800">{item.nom}</td>
                                                    <td className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-slate-700">{item.prenom}</td>
                                                    <td className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-slate-600">{item.classe}</td>
                                                    <td className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3">
                                                        <div className="flex flex-wrap gap-1">
                                                            {item.groupesReconnus.map((g) => (
                                                                <span key={g.id} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                                                                    {g.nom}
                                                                </span>
                                                            ))}
                                                            {item.groupesInconnus.map((g, i) => (
                                                                <span key={`${g}-${i}`} className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium line-through">
                                                                    {g}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-2 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3">
                                                        {item.statut === 'ok' ? (
                                                            <span className="flex items-center gap-1 text-emerald-600">
                                                                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                                OK
                                                            </span>
                                                        ) : STATUTS_SANS_ALERTE.has(item.statut) ? (
                                                            <span className="text-slate-500">{libelleStatut(item)} : non modifié</span>
                                                        ) : (
                                                            <span className="flex items-center gap-1 text-orange-600">
                                                                <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                                                {libelleStatut(item)} : non modifié
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Boutons */}
                            <div className="mt-4 sm:mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                                <button
                                    onClick={handleReset}
                                    className="flex-1 py-2.5 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 px-4 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg sm:rounded-xl hover:from-orange-600 hover:to-orange-700 transition-colors shadow-lg text-xs sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base"
                                >
                                    Réinitialiser
                                </button>

                                <button
                                    onClick={handleSubmit}
                                    disabled={stats.envoyes === 0}
                                    className={`flex-1 py-2.5 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 px-4 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 font-semibold rounded-lg sm:rounded-xl shadow-lg transition-all text-xs sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base ${
                                        stats.envoyes > 0
                                            ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                >
                                   Modifier {stats.envoyes} élève(s)
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Résultat de l'import */}
                    {importResult && (
                        <div className="mt-4 sm:mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6">
                            {/* Header du résultat */}
                            <div className={`p-3 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4 rounded-t-lg sm:rounded-t-xl ${
                                importComplet
                                    ? 'bg-emerald-500'
                                    : importResult.success
                                        ? 'bg-blue-500'
                                        : 'bg-orange-500'
                            }`}>
                                <div className="flex items-center gap-2 sm:gap-3 text-white">
                                    {importComplet ? (
                                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                                    ) : importResult.success ? (
                                        <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
                                    ) : (
                                        <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                                    )}
                                    <h3 className="font-bold text-sm sm:text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
                                        {importComplet
                                            ? 'Import réussi !'
                                            : importResult.success
                                                ? 'Import partiel'
                                                : 'Échec de l\'import'}
                                    </h3>
                                </div>
                                <p className="text-white/90 mt-1 text-xs sm:text-sm">{importResult.message}</p>
                            </div>

                            <div className="bg-slate-50 border border-slate-200 rounded-b-lg sm:rounded-b-xl p-3 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4 space-y-3 sm:space-y-4 xl:space-y-3 2xl:space-y-3.5 3xl:space-y-4">
                                {/* Élèves affectés avec succès */}
                                {importResult.elevesAffectes.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                                            <span className="font-semibold text-emerald-700 text-xs sm:text-base">
                                                {importResult.elevesAffectes.length} affectation(s) de groupe enregistrée(s)
                                            </span>
                                        </div>
                                        <div className="overflow-x-auto max-h-28 sm:max-h-32 xl:max-h-24 2xl:max-h-28 3xl:max-h-32">
                                            <table className="w-full text-[10px] sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                                                <thead className="bg-emerald-100 text-emerald-800">
                                                    <tr>
                                                        <th className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-left">Élève</th>
                                                        <th className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-left">Classe</th>
                                                        <th className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-left">Groupe</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {importResult.elevesAffectes.map((e, i) => (
                                                        <tr key={i} className="border-b border-emerald-100">
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.nom} {e.prenom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.classe}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">
                                                                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                                                                    {e.groupe_nom}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Élèves non trouvés */}
                                {importResult.elevesNonTrouves.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                                            <span className="font-semibold text-orange-700 text-xs sm:text-base">
                                                {importResult.elevesNonTrouves.length} élève(s) non trouvé(s) dans TanoEd
                                            </span>
                                        </div>
                                        <p className="text-orange-600 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm mb-2">
                                            Vérifiez l'orthographe ou importez ces élèves dans leurs classes respectives.
                                        </p>
                                        <div className="overflow-x-auto max-h-28 sm:max-h-32 xl:max-h-24 2xl:max-h-28 3xl:max-h-32">
                                            <table className="w-full text-[10px] sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                                                <thead className="bg-orange-100 text-orange-800">
                                                    <tr>
                                                        <th className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-left">Nom</th>
                                                        <th className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-left">Prénom</th>
                                                        <th className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-left">Classe</th>
                                                        <th className="px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 text-left">Raison</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {importResult.elevesNonTrouves.map((e, i) => (
                                                        <tr key={i} className="border-b border-orange-100">
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2 font-semibold">{e.nom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.prenom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.classe}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">
                                                                {e.reason === 'eleve_introuvable'
                                                                    ? 'Élève absent de TanoEd'
                                                                    : e.reason === 'groupe_introuvable'
                                                                        ? 'Groupe introuvable'
                                                                        : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Élèves hors barette */}
                                {importResult.elevesHorsBarette?.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                                            <span className="font-semibold text-amber-700 text-xs sm:text-base">
                                                {importResult.elevesHorsBarette.length} élève(s) hors barette du groupe
                                            </span>
                                        </div>
                                        <p className="text-amber-600 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm mb-2">
                                            Ces élèves ne sont pas dans une classe couverte par le groupe ciblé.
                                            Vérifiez la barette du groupe ou la classe de l'élève dans le fichier.
                                        </p>
                                        <div className="overflow-x-auto max-h-28 sm:max-h-32 xl:max-h-24 2xl:max-h-28 3xl:max-h-32">
                                            <table className="w-full text-[10px] sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                                                <thead className="bg-amber-100 text-amber-800">
                                                    <tr>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Nom</th>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Prénom</th>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Classe</th>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Groupe ciblé</th>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Barette sur Tanoed</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {importResult.elevesHorsBarette.map((e, i) => (
                                                        <tr key={i} className="border-b border-amber-100">
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2 font-semibold">{e.nom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.prenom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.classe}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.groupe_nom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">
                                                                {(e.classes_groupe || []).join(', ')}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Élèves non modifiés : une de leurs lignes est en erreur (front ou serveur) */}
                                {importResult.elevesNonModifies?.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                                            <span className="font-semibold text-orange-700 text-xs sm:text-base">
                                                {importResult.elevesNonModifies.length} élève(s) non modifié(s)
                                            </span>
                                        </div>
                                        <p className="text-orange-600 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm mb-2">
                                            Ils gardent leurs groupes actuels. Pour les modifier, corrigez leurs lignes et réimportez le fichier.
                                        </p>
                                        <div className="overflow-x-auto max-h-28 sm:max-h-32 xl:max-h-24 2xl:max-h-28 3xl:max-h-32">
                                            <table className="w-full text-[10px] sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                                                <thead className="bg-orange-100 text-orange-800">
                                                    <tr>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Nom</th>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Prénom</th>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Classe</th>
                                                        <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left">Raison</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {importResult.elevesNonModifies.map((e, i) => (
                                                        <tr key={i} className="border-b border-orange-100">
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2 font-semibold">{e.nom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.prenom}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.classe}</td>
                                                            <td className="px-2 sm:px-3 py-1.5 sm:py-2">{e.raison}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Bouton fermer */}
                                <div className="pt-3 sm:pt-4 border-t border-slate-200">
                                    <button
                                        onClick={handleCloseWithRefresh}
                                        className="w-full py-2.5 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 px-4 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 bg-gradient-to-r from-slate-600 to-slate-700 text-white font-semibold rounded-lg sm:rounded-xl hover:from-slate-700 hover:to-slate-800 transition-colors text-xs sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base"
                                    >
                                        Fermer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ImportElevesGroupe;
